import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/endpoints/auth';
import { clearAuth } from '../api';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  // P-AUTH (plan §2.1) — authentication is now cookie-based; no token
  // is readable by JS, so the bootstrap path probes /auth/me directly.
  // If the cookie is valid the server returns the user; if not it
  // returns 401 and we land in the unauthenticated state. localStorage
  // no longer holds tokens — the only persisted client state is the
  // remembered email on the login form.
  useEffect(() => {
    authApi.me()
      .then((result) => {
        const userData = result.data?.data || result.data;
        setUser(userData);
      })
      .catch((err) => {
        // Only clear auth for auth failures (401/403). Network errors
        // (no status) keep the user logged in so a flaky connection
        // doesn't kick them out.
        if (err?.status === 401 || err?.status === 403) {
          clearAuth();
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  // Listen for auth:session-expired events from httpClient (Fix 7)
  useEffect(() => {
    const handleSessionExpired = () => {
      queryClient.clear();
      setUser(null);
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, [queryClient]);

  const login = useCallback(async (email, password) => {
    const result = await authApi.login(email, password);
    // M-BE-11 — /auth/login now returns a consistent ``{message, data}``
    // mutation envelope across all three branches (no-2FA / 2FA-required /
    // 2FA-setup-required). The defensive ``result.data?.data || result.data``
    // unwrap papered over an inconsistent backend contract that M-BE-10 fixed.
    const data = result.data.data;

    // 2FA verification or setup required — return pre-2FA response to caller.
    // ON-14 adds a third such branch: an installer-seeded password that must
    // be replaced before a session exists. All three are "not signed in yet",
    // and NONE of them may call setUser — that branch's ``user`` is a bare
    // {id, email} identifier for the next step, and promoting it into auth
    // state would put a session-less half-user behind ProtectedRoute (which
    // gates on nothing but `user` being truthy, App.js:92-97).
    if (data.requires_2fa || data.requires_2fa_setup || data.requires_password_change) {
      return data;
    }

    // P-AUTH — tokens arrive as HttpOnly cookies set by the backend's
    // set_access_cookies / set_refresh_cookies. The body still includes
    // them during the migration window for the legacy header path, but
    // we deliberately don't persist them anywhere on the client. The
    // user object is held in React state only — refreshing the page
    // re-bootstraps from /auth/me.
    const userData = data.user;
    setUser(userData);
    return data;
  }, []);

  const logout = useCallback(async () => {
    // Revoke server-side token + clear cookies (fire-and-forget on
    // network failure so a flaky connection still drops local state).
    try { await authApi.logout(); } catch { /* best-effort */ }
    clearAuth();
    queryClient.clear();
    setUser(null);
  }, [queryClient]);

  // After 2FA verification / setup, the backend has already set the
  // HttpOnly cookies; we just promote the user object into React state.
  const completeLogin = useCallback((data) => {
    const userData = data.user;
    if (userData) {
      setUser(userData);
    }
  }, []);

  // Bridge for legacy Login.js — sets user directly when cookies are already set
  const loginWithToken = useCallback((userData) => {
    setUser(userData);
  }, []);

  // Re-fetch /auth/me and refresh the user object. Used after server-side
  // mutations that change fields the client cares about (2FA enable/disable,
  // email confirmed, totp_enabled toggle, etc.) so /profile reflects the
  // new state without a full page reload.
  const refreshUser = useCallback(async () => {
    try {
      const result = await authApi.me();
      const userData = result?.data?.data || result?.data || result;
      if (userData) {
        setUser(userData);
      }
      return userData;
    } catch {
      return null;
    }
  }, []);

  const hasPermission = useCallback((permission) => {
    // Source of truth is the server-returned `user.role.permissions` array
    // (Role.to_dict() at backend/models.py:539). Custom roles created via
    // POST /users/roles carry their own permissions list; resolving against a
    // hardcoded map keyed on role name made every custom-role hasPermission
    // call return false. H-RBAC-2 reverses that.
    const rolePerms = user?.role?.permissions;
    if (!Array.isArray(rolePerms)) return false;
    return rolePerms.includes(permission);
  }, [user]);

  const value = useMemo(
    () => ({ user, isLoading, login, completeLogin, loginWithToken, logout, refreshUser, hasPermission }),
    [user, isLoading, login, completeLogin, loginWithToken, logout, refreshUser, hasPermission]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
